/**
 * UI Action protocol — the ONLY way the AI (or the terminal) may drive the
 * interface. The model returns declarative actions; the frontend validates
 * every one of them against this allow-list and against the content
 * registries before executing. No generated code ever runs.
 */

import type { ZoneId } from '../lab/types';

export type UIAction =
  | { type: 'NAVIGATE'; target: string }
  | { type: 'FOCUS_ZONE'; zone: ZoneId }
  | { type: 'OPEN_PROJECT'; slug: string }
  | { type: 'OPEN_RESEARCH'; slug: string }
  | { type: 'SHOW_GRAPH'; filter?: string }
  | { type: 'FOCUS_NODE'; id: string }
  | { type: 'SHOW_AGENT'; id: string }
  | { type: 'COMPARE'; a: string; b: string };

const ALLOWED_ROUTES = new Set([
  '/',
  '/command',
  '/research',
  '/projects',
  '/agents',
  '/knowledge',
  '/publications',
  '/observatory',
  '/about',
  '/contact',
]);

const ZONE_IDS = new Set([
  'core',
  'research',
  'projects',
  'agents',
  'knowledge',
  'observatory',
  'archive',
  'gateway',
]);

export interface ActionValidators {
  isProject: (slug: string) => boolean;
  isResearch: (slug: string) => boolean;
  isNode: (id: string) => boolean;
  isAgent: (id: string) => boolean;
}

/**
 * Validate raw parsed JSON (unknown shape) into a list of safe actions.
 * Anything unrecognized is silently dropped — a hostile or hallucinated
 * action must degrade to "nothing happens", never to an error dialog.
 */
export function validateActions(raw: unknown, v: ActionValidators): UIAction[] {
  if (!Array.isArray(raw)) return [];
  const out: UIAction[] = [];
  for (const item of raw.slice(0, 6)) {
    if (!item || typeof item !== 'object') continue;
    const a = item as Record<string, unknown>;
    switch (a.type) {
      case 'NAVIGATE': {
        if (typeof a.target === 'string') {
          const t = a.target.split(/[?#]/)[0];
          if (ALLOWED_ROUTES.has(t) || isEntityRoute(t, v)) {
            out.push({ type: 'NAVIGATE', target: t });
          }
        }
        break;
      }
      case 'FOCUS_ZONE':
        if (typeof a.zone === 'string' && ZONE_IDS.has(a.zone)) {
          out.push({ type: 'FOCUS_ZONE', zone: a.zone as ZoneId });
        }
        break;
      case 'OPEN_PROJECT':
        if (typeof a.slug === 'string' && v.isProject(a.slug)) {
          out.push({ type: 'OPEN_PROJECT', slug: a.slug });
        }
        break;
      case 'OPEN_RESEARCH':
        if (typeof a.slug === 'string' && v.isResearch(a.slug)) {
          out.push({ type: 'OPEN_RESEARCH', slug: a.slug });
        }
        break;
      case 'SHOW_GRAPH':
        out.push({
          type: 'SHOW_GRAPH',
          filter: typeof a.filter === 'string' ? a.filter.slice(0, 64) : undefined,
        });
        break;
      case 'FOCUS_NODE':
        if (typeof a.id === 'string' && v.isNode(a.id)) {
          out.push({ type: 'FOCUS_NODE', id: a.id });
        }
        break;
      case 'SHOW_AGENT':
        if (typeof a.id === 'string' && v.isAgent(a.id)) {
          out.push({ type: 'SHOW_AGENT', id: a.id });
        }
        break;
      case 'COMPARE':
        if (
          typeof a.a === 'string' &&
          typeof a.b === 'string' &&
          v.isProject(a.a) &&
          v.isProject(a.b)
        ) {
          out.push({ type: 'COMPARE', a: a.a, b: a.b });
        }
        break;
    }
  }
  return out;
}

function isEntityRoute(t: string, v: ActionValidators): boolean {
  const m = t.match(/^\/(projects|research)\/([a-z0-9-]+)$/);
  if (!m) return false;
  return m[1] === 'projects' ? v.isProject(m[2]) : v.isResearch(m[2]);
}
